import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import api from '../services/api';

function Contact() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    service: '',
    preferredDate: '',
    preferredTime: 'Any available time',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending appointment inquiry...');
    setSuccess(false);
    try {
      await api.post('/contact', form);
      setSuccess(true);
      setStatus('✓ Your appointment request has been submitted successfully! An email notification has been dispatched to our clinical desk and a confirmation has been sent to your email.');
      setForm({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        service: '',
        preferredDate: '',
        preferredTime: 'Any available time',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setStatus('Unable to submit enquiry. Please verify your connection or try again later.');
    }
  };

  return (
    <>
      {/* Page Hero */}
      <section className="page-hero" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.85), rgba(8, 50, 62, 0.85)), url(/images/hero_contact.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Reach Out</div>
          <h1>Contact Us</h1>
          <p>Get in touch to book appointments, consult with our medical desk, or request support.</p>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">›</span>
            <span className="current">Contact Us</span>
          </div>
        </div>
      </section>

      {/* Main Grid: Info & Form */}
      <section className="section-pad">
        <div className="container contact-grid">
          <div>
            <div className="eyebrow">MEGA HOSPITAL FOUNDATION</div>
            <h2>Contact Mega Health Group</h2>
            <p style={{ marginTop: 12, marginBottom: 28 }}>
              <strong>CIN:</strong> U85300UP2022NPL175957<br />
              Our multi-specialty clinical facility is open for consultations, diagnostics testing, and maternity admissions during standard hours. Emergency desk remains active 24/7.
            </p>
            
            <div style={{ display: 'grid', gap: 20, marginBottom: 40 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ width: 44, height: 44, background: 'var(--teal-soft)', display: 'grid', placeItems: 'center', borderRadius: '50%', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="var(--teal-primary)" strokeWidth="2.2" fill="none"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase' }}>Phone Hotline</div>
                  <a href={`tel:${(settings?.phone || '05368 297433').replace(/\s+/g, '')}`} style={{ fontWeight: 700, color: 'var(--teal-dark)' }}>
                    {settings?.phone || '05368 297433'}
                  </a>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <span style={{ width: 44, height: 44, background: 'var(--teal-soft)', display: 'grid', placeItems: 'center', borderRadius: '50%', flexShrink: 0, marginTop: 2 }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="var(--teal-primary)" strokeWidth="2.2" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase' }}>Email Support</div>
                  {(() => {
                    const primary = settings?.email || 'info@megahealthgroup.org';
                    const secondary = settings?.secondaryEmail || 'HR@megahealthgroup.org, Megafoundation022@gmail.com';
                    const extraList = secondary.split(/[\n,]+/).map(e => e.trim()).filter(Boolean);
                    const emailList = Array.from(new Set([primary, ...extraList]));
                    return emailList.map((em, idx) => (
                      <a key={idx} href={`mailto:${em}`} style={{ display: 'block', fontWeight: 700, color: 'var(--teal-dark)' }}>
                        {em}
                      </a>
                    ));
                  })()}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ width: 44, height: 44, background: 'var(--teal-soft)', display: 'grid', placeItems: 'center', borderRadius: '50%', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="var(--teal-primary)" strokeWidth="2.2" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase' }}>Head Office Address</div>
                  <div style={{ fontWeight: 700, color: 'var(--teal-dark)' }}>
                    {settings?.address || 'Head Office: F19, First Floor Ved Vihar Ghaziabad, Uttar Pradesh, INDIA'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ width: 44, height: 44, background: 'var(--teal-soft)', display: 'grid', placeItems: 'center', borderRadius: '50%', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="var(--teal-primary)" strokeWidth="2.2" fill="none"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase' }}>Business Hours</div>
                  <div style={{ fontWeight: 700, color: 'var(--teal-dark)' }}>
                    {settings?.hours || '24X7'}
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            {settings?.googleMapsUrl && (
              <div style={{ width: '100%', height: '350px', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                <iframe
                  title="Mega Health Group Location Map"
                  src={settings.googleMapsUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            )}
          </div>

          {/* Contact / Appointment form */}
          <div className="card">
            <div className="eyebrow">Schedule Slot</div>
            <h2 style={{ fontSize: '1.65rem' }}>Book Medical Appointment</h2>
            <p style={{ fontSize: '0.88rem', margin: '8px 0 24px' }}>Submit your request below, and our coordinator will contact you to align the schedule.</p>
            
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>First Name *</label>
                  <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" required />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" required />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" required />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address" />
              </div>

              <div className="form-group">
                <label>Medical Specialty Required *</label>
                <select name="service" value={form.service} onChange={handleChange} required>
                  <option value="">Select specialty...</option>
                  <option value="Eye Care">Eye Care Specialist</option>
                  <option value="Maternity Care">Maternity & Gynecology</option>
                  <option value="Pediatrician">Pediatrician / Child Specialist</option>
                  <option value="Cosmetic Gynecology">Cosmetic Gynecology</option>
                  <option value="ENT Specialist">ENT (Ear, Nose & Throat)</option>
                  <option value="Dental Clinic">Dental / Oral Care</option>
                  <option value="Blood Test Services">Pathology Blood Tests</option>
                  <option value="Ultrasound Imaging">Ultrasound & Imaging Scan</option>
                  <option value="General Enquiry">General / Other Inquiry</option>
                </select>
              </div>

              <div className="form-group">
                <label>Preferred Appointment Date</label>
                <input name="preferredDate" type="date" value={form.preferredDate} onChange={handleChange} min={new Date().toISOString().split('T')[0]} />
              </div>

              <div className="form-group">
                <label>Preferred Time Slot</label>
                <select name="preferredTime" value={form.preferredTime} onChange={handleChange}>
                  <option value="Any available time">Any available time</option>
                  <option value="Morning (8:00 AM – 12:00 PM)">Morning (8:00 AM – 12:00 PM)</option>
                  <option value="Afternoon (12:00 PM – 4:00 PM)">Afternoon (12:00 PM – 4:00 PM)</option>
                  <option value="Evening (4:00 PM – 8:00 PM)">Evening (4:00 PM – 8:00 PM)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes / Medical Concern</label>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder="Briefly describe your medical concerns, current treatments, or requirements..."></textarea>
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}>Request Slot</button>

              {status && (
                <p style={{ marginTop: 16, textAlign: 'center', fontSize: '0.9rem', color: success ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                  {status}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
